import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import DataValidationUtil from "../../utils/DataValidationUtil.ts";
import { useAuth } from "../../provider/AuthProvider.tsx";

const Login: React.FC = () => {
  const { logIn } = useAuth();
  const isBeta =
    // @ts-ignore
    import.meta.env.MODE === "development" ||
    process.env.NODE_ENV === "development";

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [usernameError, setUsernameError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();

  const validateForm = () => {
    const usernameValidation = DataValidationUtil.validateUsername(username);
    const passwordValidation = DataValidationUtil.validatePassword(password);
    setUsernameError(usernameValidation.error);
    setPasswordError(passwordValidation.error);
    return usernameValidation.isValid && passwordValidation.isValid;
  };

  const logPersonIn = async (): Promise<void> => {
    if (!validateForm()) return;
    try {
      await logIn(username, password);
      navigate("/", { replace: true });
    } catch (error: any) {
      setError(error.message || "Възникна неочаквана грешка");
    }
  };

  const inputClass =
    "border-stroke focus:border-primary dark:border-form-strokedark dark:bg-form-input dark:focus:border-primary w-full rounded-lg border bg-transparent py-3 pl-4 pr-10 text-sm font-semibold text-gray-900 outline-none focus-visible:shadow-none dark:text-white";

  return (
    <div className="flex min-h-screen flex-col items-center justify-center">
      <div className="border-stroke shadow-default dark:border-strokedark dark:bg-boxdark rounded-sm border bg-white">
        <div className="flex flex-wrap items-center">
          <div className="border-stroke dark:border-strokedark w-full xl:border-l-2">
            <div className="w-full p-6 sm:p-10">
              <span className="mb-1.5 block font-semibold text-gray-900 dark:text-white">
                Libra
              </span>
              <h2 className="mb-8 text-xl font-bold text-gray-900 dark:text-white sm:text-2xl">
                Вход в Web Insurance
                {isBeta && (
                  <p className="text-secondary-light text-sm italic">Beta</p>
                )}
              </h2>

              {error && (
                <div className="mb-4 text-rose-500">
                  <p className="text-sm font-semibold">{error}</p>
                </div>
              )}

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  logPersonIn();
                }}
              >
                {/* Username */}
                <div className="mb-4">
                  <label className="mb-2 block text-sm font-bold text-gray-900 dark:text-white">
                    Потребител
                  </label>
                  <div className="relative">
                    <input
                      autoComplete="on"
                      type="text"
                      placeholder="Потребителско име"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      className={`${inputClass} ${
                        usernameError ? "border-rose-500" : ""
                      }`}
                    />
                    <i className="fa-regular fa-user absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-400" />
                  </div>
                  {usernameError && (
                    <p className="mt-1 text-xs font-semibold text-rose-500">
                      {usernameError}
                    </p>
                  )}
                </div>

                {/* Password */}
                <div className="mb-6">
                  <label className="mb-2 block text-sm font-bold text-gray-900 dark:text-white">
                    Парола
                  </label>
                  <div className="relative">
                    <input
                      autoComplete="on"
                      type={showPassword ? "text" : "password"}
                      placeholder="Въведете парола"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className={`${inputClass} ${
                        passwordError ? "border-rose-500" : ""
                      }`}
                    />
                    <button
                      type="button"
                      tabIndex={-1}
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-400 hover:text-gray-600 focus:outline-none"
                      aria-label={
                        showPassword ? "Скрий паролата" : "Покажи паролата"
                      }
                    >
                      <i
                        className={`fa-regular ${
                          showPassword ? "fa-eye-slash" : "fa-eye"
                        }`}
                      />
                    </button>
                  </div>
                  {passwordError && (
                    <p className="mt-1 text-xs font-semibold text-rose-500">
                      {passwordError}
                    </p>
                  )}
                </div>

                <div className="mb-5">
                  <input
                    type="submit"
                    value="Вход"
                    className="border-primary bg-primary w-full cursor-pointer rounded-lg border py-3 text-sm font-semibold text-white transition hover:bg-opacity-90"
                  />
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
